import { Alert, Button, Card, CardBody, CardHeader, Checkbox, Chip, Divider, Input, Progress, Select, SelectItem, Tooltip } from '@heroui/react';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, RotateCcw } from 'lucide-react';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ContentLoadError, ModalConfirmation, PageTitle, PageWrapper, Spinner } from '../../../components';
import { useHeroToast } from '../../../hooks';
import {
  AI_CHANNEL_HINTS,
  AI_CHANNEL_LABELS,
  AI_CHANNELS,
  AiChannel,
  AiChannelPatch,
  AiChannelState,
  ORG_AI_LIMITS_QUERY_KEY,
  useGetOrgAiLimits,
  useResetOrgAiUsage,
  useSetOrgAiLimits,
} from '../../../queries';

/**
 * Limity zużycia AI organizacji: budżet w USD osobno dla wiadomości tekstowych,
 * głosówek i rozmów live.
 *
 * Ekran jest w hs-adminie, bo budżet decyduje o tym, ile kosztu u dostawcy może
 * wygenerować klient. Panel hotelu widzi zużycie, ale limitu nie ustawia.
 *
 * Zużycie zeruje cron 28. dnia miesiąca. Przycisk na dole robi to samo ręcznie
 * dla tej jednej organizacji, gdy okres trzeba domknąć wcześniej.
 */

/** Puste pole = „nie zmieniaj", nie „ustaw zero" — dlatego formularz trzyma stringi. */
type ChannelForm = {
  limit: string;
  model: string;
  marginMultiplier: string;
  maxCallSeconds: string;
  maxConcurrentSessions: string;
  maxOutputTokens: string;
  voiceProvider: string;
};

const EMPTY_FORM: ChannelForm = {
  limit: '',
  model: '',
  marginMultiplier: '',
  maxCallSeconds: '',
  maxConcurrentSessions: '',
  maxOutputTokens: '',
  voiceProvider: '',
};

/**
 * Gotowe budżety do jednego kliknięcia, każdy z własnym kompletem ustawień.
 *
 * TO NIE JEST SAMA KWOTA. Budżet i limity rozmowy live to jedna decyzja: 10 dolarów
 * z pięciominutowymi rozmowami i dwoma równoległymi połączeniami skończy się w tydzień,
 * a 40 dolarów z trzyminutowym limitem marnuje pieniądze, których nie da się wydać.
 * Dlatego każdy próg niesie własny profil, a kliknięcie NADPISUJE poprzedni: po wyborze
 * 40 i powrocie na 20 w formularzu stoją ustawienia progu 20, nie resztki po czterdziestce.
 *
 * MODEL WPISUJEMY WPROST, per kanał (`DEFAULT_MODELS`). Puste pole też działa (backend
 * bierze wtedy swój domyślny), ale w hs-adminie nie widać, co się pod nim kryje, a to
 * jest ekran, na którym ustala się koszt. Przy zmianie modelu w backendzie trzeba
 * poprawić tę mapę — inaczej preset będzie wpisywał model sprzed zmiany.
 */
interface LimitPreset {
  amount: number;
  /** Podpowiedź, dla kogo ten próg — widoczna w dymku przycisku. */
  hint: string;
  realtime: {
    /**
     * Model rozmowy live rośnie razem z budżetem: progi próbny i standardowy jadą na
     * `mini`, a przy 40 dolarach wchodzi pełny `gpt-realtime-2.1`. Pełny brzmi lepiej,
     * ale kosztuje wielokrotnie więcej za minutę i na dziesiątce wyczerpałby budżet
     * w kilka rozmów.
     */
    model: string;
    maxCallSeconds: string;
    maxConcurrentSessions: string;
    maxOutputTokens: string;
  };
}

const LIMIT_PRESETS: LimitPreset[] = [
  {
    amount: 10,
    hint: 'Próbny: krótkie rozmowy, jedno połączenie naraz.',
    realtime: {
      model: 'gpt-realtime-2.1-mini', maxCallSeconds: '180', maxConcurrentSessions: '1', maxOutputTokens: '512',
    },
  },
  {
    amount: 20,
    hint: 'Standardowy: pięciominutowe rozmowy, jedno połączenie naraz.',
    realtime: {
      model: 'gpt-realtime-2.1-mini', maxCallSeconds: '300', maxConcurrentSessions: '1', maxOutputTokens: '1024',
    },
  },
  {
    amount: 40,
    hint: 'Intensywny: pełny model rozmowy live, dłuższe rozmowy i dwa połączenia naraz.',
    realtime: {
      model: 'gpt-realtime-2.1', maxCallSeconds: '420', maxConcurrentSessions: '2', maxOutputTokens: '1536',
    },
  },
];

/** Domyślny narzut nad kosztem dostawcy: 10 procent. Wspólny dla wszystkich progów. */
const DEFAULT_MARGIN = '1.1';

/**
 * Modele domyślne per kanał — te same, których backend używa bez ustawienia hotelu
 * (`functions-ai/src/config/models.ts` i `api/voice/realtimeConfig.ts`).
 */
const DEFAULT_MODELS: Record<AiChannel, string> = {
  chat: 'gpt-5-mini',
  tts: 'gpt-4o-mini-tts',
  // Rozmowa live bierze model z PROGU, nie stąd — ta wartość służy tylko jako
  // podpowiedź w pustym polu (patrz `LimitPreset.realtime.model`).
  realtime: 'gpt-realtime-2.1-mini',
};

/** Dostawca głosu w rozmowie live. ElevenLabs jako domyślny wybór jakościowy. */
const DEFAULT_VOICE_PROVIDER = 'elevenlabs';

/** Profil progu środkowego — z niego biorą się podpowiedzi w pustych polach. */
const DEFAULT_PRESET = LIMIT_PRESETS[1];

const VOICE_PROVIDERS = [
  { key: 'openai', label: 'OpenAI (mowa z modelu)' },
  { key: 'elevenlabs', label: 'ElevenLabs' },
];

const toForm = (state: AiChannelState): ChannelForm => ({
  limit: String(state.limitUsd ?? 0),
  model: state.model,
  marginMultiplier: state.marginMultiplier === null ? '' : String(state.marginMultiplier),
  maxCallSeconds: state.realtime?.maxCallSeconds === null || state.realtime?.maxCallSeconds === undefined
    ? '' : String(state.realtime.maxCallSeconds),
  maxConcurrentSessions: state.realtime?.maxConcurrentSessions === null || state.realtime?.maxConcurrentSessions === undefined
    ? '' : String(state.realtime.maxConcurrentSessions),
  maxOutputTokens: state.realtime?.maxOutputTokens === null || state.realtime?.maxOutputTokens === undefined
    ? '' : String(state.realtime.maxOutputTokens),
  voiceProvider: state.realtime?.voiceProvider ?? '',
});

const toNumber = (value: string): number => Number(value.replace(',', '.'));

const isValidAmount = (value: string, max: number): boolean => {
  if (value.trim() === '') return true;
  const parsed = toNumber(value);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= max;
};

const usd = (value: number): string => `$${value.toFixed(2)}`;

/** Pasek robi się pomarańczowy przy 80 procentach, żeby limit nie zaskakiwał dopiero po wyczerpaniu. */
const usageColor = (percent: number): 'danger' | 'warning' | 'primary' => {
  if (percent >= 100) return 'danger';
  if (percent >= 80) return 'warning';
  return 'primary';
};

/** Pola formularza, które realnie różnią się od stanu z serwera. */
const buildPatch = (channel: AiChannel, form: ChannelForm, state: AiChannelState): AiChannelPatch | null => {
  const patch: AiChannelPatch = {};

  if (form.limit.trim() !== '' && toNumber(form.limit) !== state.limitUsd) {
    patch.limit = toNumber(form.limit);
  }
  if (form.model.trim() !== state.model) patch.model = form.model.trim();
  if (form.marginMultiplier.trim() !== '' && toNumber(form.marginMultiplier) !== state.marginMultiplier) {
    patch.marginMultiplier = toNumber(form.marginMultiplier);
  }

  if (channel === 'realtime') {
    const rt = state.realtime;
    if (form.maxCallSeconds.trim() !== '' && toNumber(form.maxCallSeconds) !== rt?.maxCallSeconds) {
      patch.maxCallSeconds = toNumber(form.maxCallSeconds);
    }
    if (form.maxConcurrentSessions.trim() !== '' && toNumber(form.maxConcurrentSessions) !== rt?.maxConcurrentSessions) {
      patch.maxConcurrentSessions = toNumber(form.maxConcurrentSessions);
    }
    if (form.maxOutputTokens.trim() !== '' && toNumber(form.maxOutputTokens) !== rt?.maxOutputTokens) {
      patch.maxOutputTokens = toNumber(form.maxOutputTokens);
    }
    if (form.voiceProvider !== (rt?.voiceProvider ?? '')) patch.voiceProvider = form.voiceProvider;
  }

  return Object.keys(patch).length ? patch : null;
};

interface ChannelCardProps {
  state: AiChannelState;
  form: ChannelForm;
  onChange: (field: keyof ChannelForm, value: string) => void;
}

const ChannelCard: FC<ChannelCardProps> = ({ state, form, onChange }) => {
  const { channel } = state;
  const used = state.limitUsd > 0 ? Math.min(100, (state.usageUsd / state.limitUsd) * 100) : 0;
  const isRealtime = channel === 'realtime';

  /* Preset NADPISUJE cały formularz kanału, także po wcześniejszym kliknięciu innego
     progu: model i dostawca głosu wracają do wartości domyślnych, nie do poprzednich. */
  const applyPreset = (preset: LimitPreset): void => {
    onChange('limit', String(preset.amount));
    onChange('marginMultiplier', DEFAULT_MARGIN);
    // Rozmowa live ma model zależny od progu; pozostałe kanały biorą swój domyślny.
    onChange('model', isRealtime ? preset.realtime.model : DEFAULT_MODELS[channel]);
    if (!isRealtime) return;
    onChange('voiceProvider', DEFAULT_VOICE_PROVIDER);
    onChange('maxCallSeconds', preset.realtime.maxCallSeconds);
    onChange('maxConcurrentSessions', preset.realtime.maxConcurrentSessions);
    onChange('maxOutputTokens', preset.realtime.maxOutputTokens);
  };

  return (
    <Card shadow="sm" className="w-full">
      <CardHeader className="flex items-start justify-between gap-4">
        <div>
          <p className="text-medium font-semibold">{AI_CHANNEL_LABELS[channel]}</p>
          <p className="text-tiny text-default-500 mt-1 max-w-[520px]">{AI_CHANNEL_HINTS[channel]}</p>
        </div>
        <Chip size="sm" color={state.enabled ? 'success' : 'danger'} variant="flat">
          {state.enabled ? 'Aktywny' : 'Zablokowany'}
        </Chip>
      </CardHeader>

      <Divider />

      <CardBody className="gap-5">
        {!state.exists && (
          <Alert
            color="warning"
            title="Kanał nie jest jeszcze skonfigurowany"
            description="Zapis ustawień utworzy dokument budżetu, nawet jeśli organizacja nie ma agenta."
          />
        )}

        <div>
          <div className="flex justify-between text-tiny text-default-500 mb-1">
            <span>
              Zużyto
              {' '}
              {usd(state.usageUsd)}
              {state.limitUsd > 0 ? ` z ${usd(state.limitUsd)}` : ' (bez limitu)'}
            </span>
            <span>{state.limitUsd > 0 ? `pozostało ${usd(state.remainingUsd)}` : ''}</span>
          </div>
          <Progress
            aria-label="Zużycie budżetu"
            value={used}
            color={usageColor(used)}
            size="sm"
          />
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-tiny text-default-500">Szybki budżet</span>
          {LIMIT_PRESETS.map((preset) => (
            <Tooltip key={preset.amount} content={preset.hint} size="sm" radius="sm" delay={300}>
              <Button
                size="sm"
                variant={form.limit === String(preset.amount) ? 'solid' : 'bordered'}
                color={form.limit === String(preset.amount) ? 'primary' : 'default'}
                onPress={() => applyPreset(preset)}
              >
                {`$${preset.amount}`}
              </Button>
            </Tooltip>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            type="text"
            label="Limit miesięczny"
            description="0 oznacza brak budżetu. Dla głosówek i rozmów live to równocześnie wyłączenie kanału."
            value={form.limit}
            onValueChange={(v) => onChange('limit', v)}
            startContent={<span className="text-default-400 text-sm">$</span>}
            isInvalid={!isValidAmount(form.limit, 100000)}
            errorMessage="Podaj kwotę z zakresu 0 do 100000"
          />
          <Input
            type="text"
            label="Model"
            description="Puste pole przywraca model domyślny z konfiguracji backendu."
            placeholder={DEFAULT_MODELS[channel]}
            value={form.model}
            onValueChange={(v) => onChange('model', v)}
          />
          <Input
            type="text"
            label="Marża"
            description="Mnożnik nad kosztem dostawcy. 1.10 oznacza 10 procent narzutu."
            placeholder={DEFAULT_MARGIN}
            value={form.marginMultiplier}
            onValueChange={(v) => onChange('marginMultiplier', v)}
            isInvalid={form.marginMultiplier.trim() !== '' && (toNumber(form.marginMultiplier) < 1 || toNumber(form.marginMultiplier) > 5)}
            errorMessage="Podaj wartość z zakresu 1 do 5"
          />

          {isRealtime && (
            <>
              <Select
                label="Dostawca głosu"
                description="Bez wybranego dostawcy rozmowa live jest wyłączona."
                selectedKeys={form.voiceProvider ? [form.voiceProvider] : []}
                onSelectionChange={(keys) => onChange('voiceProvider', String(Array.from(keys)[0] ?? ''))}
              >
                {VOICE_PROVIDERS.map((provider) => (
                  <SelectItem key={provider.key}>{provider.label}</SelectItem>
                ))}
              </Select>
              <Input
                type="text"
                label="Maksymalna długość rozmowy"
                description="Sekundy, zakres 60 do 600."
                placeholder={DEFAULT_PRESET.realtime.maxCallSeconds}
                value={form.maxCallSeconds}
                onValueChange={(v) => onChange('maxCallSeconds', v)}
                isInvalid={!isValidAmount(form.maxCallSeconds, 600)}
                errorMessage="Podaj wartość z zakresu 60 do 600"
              />
              <Input
                type="text"
                label="Równoległe rozmowy"
                description="Ile połączeń naraz, zakres 1 do 3."
                placeholder={DEFAULT_PRESET.realtime.maxConcurrentSessions}
                value={form.maxConcurrentSessions}
                onValueChange={(v) => onChange('maxConcurrentSessions', v)}
                isInvalid={!isValidAmount(form.maxConcurrentSessions, 3)}
                errorMessage="Podaj wartość z zakresu 1 do 3"
              />
              <Input
                type="text"
                label="Limit długości odpowiedzi"
                description="Tokeny, zakres 256 do 4096."
                placeholder={DEFAULT_PRESET.realtime.maxOutputTokens}
                value={form.maxOutputTokens}
                onValueChange={(v) => onChange('maxOutputTokens', v)}
                isInvalid={!isValidAmount(form.maxOutputTokens, 4096)}
                errorMessage="Podaj wartość z zakresu 256 do 4096"
              />
            </>
          )}
        </div>

        <div className="flex flex-wrap gap-2 text-tiny text-default-500">
          <span>
            Czat:
            {' '}
            {usd(state.breakdown.chatUsd)}
          </span>
          <span>
            Synteza:
            {' '}
            {usd(state.breakdown.ttsUsd)}
          </span>
          <span>
            Transkrypcja:
            {' '}
            {usd(state.breakdown.sttUsd)}
          </span>
          <span>
            Rozmowy live:
            {' '}
            {usd(state.breakdown.realtimeUsd)}
          </span>
          {state.lastResetAt && (
            <span>
              Ostatni reset:
              {' '}
              {new Date(state.lastResetAt).toLocaleString('pl-PL')}
            </span>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

export const AiLimits: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useHeroToast();

  const { data, isLoading, error, refetch } = useGetOrgAiLimits(id);
  const { mutateAsync: saveLimits, isPending: isSaving } = useSetOrgAiLimits();
  const { mutateAsync: resetUsage, isPending: isResetting } = useResetOrgAiUsage();

  const [forms, setForms] = useState<Record<AiChannel, ChannelForm>>({
    chat: EMPTY_FORM, tts: EMPTY_FORM, realtime: EMPTY_FORM,
  });
  const [isResetModalOpen, setResetModalOpen] = useState(false);
  const [autoReset, setAutoReset] = useState(false);

  useEffect(() => {
    if (!data) return;
    setForms(data.channels.reduce(
      (acc, state) => ({ ...acc, [state.channel]: toForm(state) }),
      { chat: EMPTY_FORM, tts: EMPTY_FORM, realtime: EMPTY_FORM },
    ));
    setAutoReset(data.autoResetMonthly);
  }, [data]);

  const byChannel = useMemo(
    () => new Map((data?.channels ?? []).map((state) => [state.channel, state])),
    [data],
  );

  const changes = useMemo(() => {
    if (!data) return {} as Partial<Record<AiChannel, AiChannelPatch>>;
    return AI_CHANNELS.reduce((acc, channel) => {
      const state = byChannel.get(channel);
      if (!state) return acc;
      const patch = buildPatch(channel, forms[channel], state);
      return patch ? { ...acc, [channel]: patch } : acc;
    }, {} as Partial<Record<AiChannel, AiChannelPatch>>);
  }, [data, byChannel, forms]);

  const autoResetChanged = !!data && autoReset !== data.autoResetMonthly;
  const changedCount = Object.keys(changes).length + (autoResetChanged ? 1 : 0);

  const handleChange = (channel: AiChannel) => (field: keyof ChannelForm, value: string): void => {
    setForms((prev) => ({ ...prev, [channel]: { ...prev[channel], [field]: value } }));
  };

  const handleSave = async (): Promise<void> => {
    if (!id || changedCount === 0) return;
    try {
      await saveLimits({
        orgId: id,
        channels: changes,
        // Pomijamy pole, gdy checkbox nie ruszony — backend traktuje brak jako „bez zmiany".
        ...(autoResetChanged ? { autoResetMonthly: autoReset } : {}),
      });
      await queryClient.invalidateQueries({ queryKey: [ORG_AI_LIMITS_QUERY_KEY, id] });
      showSuccess('Limity AI zapisane');
    } catch (e: any) {
      showError(e?.message || 'Nie udało się zapisać limitów');
    }
  };

  const handleReset = async (): Promise<void> => {
    if (!id) return;
    setResetModalOpen(false);
    try {
      const result = await resetUsage({ orgId: id });
      await queryClient.invalidateQueries({ queryKey: [ORG_AI_LIMITS_QUERY_KEY, id] });
      showSuccess(`Zużycie wyzerowane (${usd(result.clearedUsd)} zarchiwizowane w okresie ${result.period})`);
    } catch (e: any) {
      showError(e?.message || 'Nie udało się wyzerować zużycia');
    }
  };

  if (isLoading) return <Spinner global />;
  if (error || !data) {
    return (
      <PageWrapper>
        <ContentLoadError onRefreshClicked={() => { refetch(); }} message={error?.message} />
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <ModalConfirmation
        needConfirm={false}
        confirmValue=""
        isOpen={isResetModalOpen}
        onClose={() => setResetModalOpen(false)}
        onConfirm={handleReset}
        title="Wyzerować zużycie AI?"
        description="Liczniki wszystkich trzech kanałów wrócą do zera. Zużycie sprzed resetu zostanie zapisane w historii okresu, a budżety i ustawienia pozostaną bez zmian."
        buttonConfirm="Wyzeruj"
      />

      <PageTitle
        title={`Limity AI: ${data.organizationName}`}
        actions={(
          <Button
            size="sm"
            variant="flat"
            color="warning"
            startContent={<RotateCcw size={14} />}
            isDisabled={isResetting}
            onPress={() => setResetModalOpen(true)}
          >
            {isResetting ? 'Zerowanie...' : 'Wyzeruj zużycie'}
          </Button>
        )}
      />

      <p className="text-sm text-default-500 mt-2 mb-6 max-w-[820px]">
        Budżet w dolarach, osobno dla każdego kanału AI. Po wyczerpaniu budżetu kanał przestaje
        być dostępny dla gości. Zużycie liczone jest po stawkach z zakładki Cennik AI.
      </p>

      <div className="flex flex-wrap gap-6 mb-6 text-sm">
        <span>
          <strong>Suma budżetów:</strong>
          {' '}
          {usd(data.totals.limitUsd)}
        </span>
        <span>
          <strong>Suma zużycia:</strong>
          {' '}
          {usd(data.totals.usageUsd)}
        </span>
      </div>

      <Card shadow="sm" className="max-w-[900px] mb-5">
        <CardBody>
          <Checkbox isSelected={autoReset} onValueChange={setAutoReset}>
            Resetuj zużycie co miesiąc
          </Checkbox>
          <p className="text-tiny text-default-500 mt-2 ml-7 max-w-[640px]">
            Zaznaczone: 28. dnia miesiąca liczniki zużycia wracają do zera, a budżety
            i ustawienia zostają bez zmian. Odznaczone: organizacja jest pomijana przez
            automat, więc budżet działa jak jednorazowa pula do wyczerpania.
          </p>
        </CardBody>
      </Card>

      <div className="flex flex-col gap-5 max-w-[900px]">
        {AI_CHANNELS.map((channel) => {
          const state = byChannel.get(channel);
          if (!state) return null;
          return (
            <ChannelCard
              key={channel}
              state={state}
              form={forms[channel]}
              onChange={handleChange(channel)}
            />
          );
        })}
      </div>

      <div className="flex items-center gap-3 mt-8">
        <Button color="primary" onPress={handleSave} isDisabled={changedCount === 0 || isSaving}>
          {isSaving ? 'Zapisywanie...' : `Zapisz${changedCount ? ` (${changedCount})` : ''}`}
        </Button>
        <Button variant="light" startContent={<ArrowLeft size={14} />} onPress={() => navigate('/organizations')}>
          Wróć do listy
        </Button>
      </div>
    </PageWrapper>
  );
};
